import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const FinanceCalculatorPage = () => {
  const [calcType, setCalcType] = useState('fv');
  const [pv, setPv] = useState('10000');
  const [fv, setFv] = useState('');
  const [rate, setRate] = useState('5');
  const [periods, setPeriods] = useState('10');
  const [pmt, setPmt] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const pvVal = parseFloat(pv) || 0;
    const fvVal = parseFloat(fv) || 0;
    const r = (parseFloat(rate) || 0) / 100;
    const n = parseFloat(periods) || 1;
    const pmtVal = parseFloat(pmt) || 0;

    let res;
    switch (calcType) {
      case 'fv': // Future Value
        res = pvVal * Math.pow(1 + r, n) + pmtVal * ((Math.pow(1 + r, n) - 1) / r);
        setResult({ type: 'Future Value', value: res });
        break;
      case 'pv': // Present Value
        res = fvVal / Math.pow(1 + r, n);
        setResult({ type: 'Present Value', value: res });
        break;
      case 'rate': // Interest Rate
        res = (Math.pow(fvVal / pvVal, 1 / n) - 1) * 100;
        setResult({ type: 'Interest Rate', value: res, isRate: true });
        break;
      case 'periods': // Number of Periods
        res = Math.log(fvVal / pvVal) / Math.log(1 + r);
        setResult({ type: 'Number of Periods', value: res, isPeriods: true });
        break;
      case 'pmt': // Payment
        res = (fvVal - pvVal * Math.pow(1 + r, n)) / ((Math.pow(1 + r, n) - 1) / r);
        setResult({ type: 'Payment', value: res });
        break;
      default: break;
    }
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link><span className="mx-2">›</span>
            <span className="text-gray-400">Finance Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-4">Finance Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate Future Value, Present Value, Interest Rate, Periods, or Payment for time value of money calculations.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex flex-wrap border-b border-[#2a2a2a] mb-6 gap-1">
              {[{ id: 'fv', label: 'FV' }, { id: 'pv', label: 'PV' }, { id: 'rate', label: 'Rate' }, { id: 'periods', label: 'N' }, { id: 'pmt', label: 'PMT' }].map(c => (
                <button key={c.id} onClick={() => setCalcType(c.id)}
                  className={`px-4 py-2 text-sm border-b-2 ${calcType === c.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                  Calculate {c.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {calcType !== 'pv' && calcType !== 'rate' && calcType !== 'periods' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Present Value</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={pv} onChange={(e) => setPv(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
                </div>
              )}
              {(calcType === 'pv' || calcType === 'rate' || calcType === 'periods' || calcType === 'pmt') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Future Value</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={fv} onChange={(e) => setFv(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
                </div>
              )}
              {calcType !== 'rate' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Interest Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" /><span className="text-gray-400">%</span></div>
                </div>
              )}
              {calcType !== 'periods' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Periods</label>
                  <Input type="number" value={periods} onChange={(e) => setPeriods(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              )}
              {calcType === 'fv' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Payment (PMT)</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={pmt} onChange={(e) => setPmt(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" /></div>
                </div>
              )}
              {(calcType === 'rate' || calcType === 'periods' || calcType === 'pmt') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Present Value</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={pv} onChange={(e) => setPv(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
                </div>
              )}
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl text-center">
                <p className="text-gray-400 mb-2">{result.type}</p>
                <p className="text-4xl font-bold text-green-400">
                  {result.isRate ? `${result.value.toFixed(4)}%` : result.isPeriods ? result.value.toFixed(2) : formatCurrency(result.value)}
                </p>
              </div>
            )}
          </div>
          <AdInContent />
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Investment', path: '/investment-calculator' }, { name: 'Compound Interest', path: '/compound-interest-calculator' }, { name: 'Loan', path: '/loan-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FinanceCalculatorPage;
